import {EditorView} from '@codemirror/view'
import {
  BoldIcon,
  CodeIcon,
  CodeSquareIcon,
  ItalicIcon,
  LinkIcon,
  ListOrderedIcon,
  ListUnorderedIcon,
  QuoteIcon,
  StrikethroughIcon,
  TasklistIcon,
} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import type {List, ListItem, Nodes} from 'mdast'

import {formatCodeBlock} from '../codemirror/commands/code-blocks/format-code-block'
import {isLabeledLink} from '../codemirror/commands/inline-formatting/format-link'
import {getListSelection, type ListItemsSelection} from '../codemirror/commands/lists/utils'
import {
  formatBlockquoteKeybinding,
  formatBoldKeybinding,
  formatInlineCodeKeybinding,
  formatItalicKeybinding,
  formatLinkKeybinding,
  formatOrderedListKeybinding,
  formatStrikethroughKeybinding,
  formatTaskListKeybinding,
  formatUnorderedListKeybinding,
} from '../codemirror/extensions/markdown-keymap'
import type {SelectionContextValue} from '../codemirror/extensions/selection-context'
import {findItemPrefixes} from '../language-tools/markdown/list-utils'
import {HeadingMenu} from './HeadingMenu'
import styles from './Toolbar.module.css'
import {ToolbarButton} from './ToolbarButton'

interface ToolbarProps {
  view: EditorView | null
  fileUploadButton: React.ReactNode
}

export const Toolbar = ({view, fileUploadButton}: ToolbarProps) => {
  const isListItem = (node: Nodes): node is ListItem => node.type === 'listItem'
  const isTaskListItem = (node: Nodes): node is ListItem => isListItem(node) && !!findItemPrefixes(node).taskListPrefix
  const isTaskList = (node: Nodes): node is List =>
    node.type === 'list' && node.children.some(child => isTaskListItem(child as Nodes))

  type SelectionListType = 'ordered' | 'unordered' | 'task'

  const getSelectionTypeFromListSelection = (selection: ListItemsSelection): SelectionListType | null => {
    const parentList = selection.list
    const types = new Set<SelectionListType>()

    for (const item of selection.selectedItems) {
      if (isTaskListItem(item)) {
        types.add('task')
      } else {
        types.add(parentList.ordered ? 'ordered' : 'unordered')
      }

      if (types.size > 1) {
        return null
      }
    }

    return types.values().next().value ?? null
  }

  const getSelectionListTypeFromContext = (selectionContext: SelectionContextValue): SelectionListType | null => {
    for (let index = selectionContext.length - 1; index >= 0; index -= 1) {
      const node = selectionContext[index]
      if (!node || node.type !== 'listItem') continue

      if (isTaskListItem(node)) {
        return 'task'
      }

      const rawPrefix = findItemPrefixes(node).listItemPrefix?.raw ?? ''
      return /^\s*(?:\d+\.|\d+\))/u.test(rawPrefix) ? 'ordered' : 'unordered'
    }

    return null
  }

  const listSelection = view ? getListSelection(view.state) : null

  const resolveListIsPressed =
    (targetType: SelectionListType) => (selectionContext: SelectionContextValue, defaultPressed: boolean) => {
      const selectionListType =
        getSelectionListTypeFromContext(selectionContext) ??
        (listSelection ? getSelectionTypeFromListSelection(listSelection) : null)

      if (!defaultPressed) {
        return false
      }

      if (!selectionListType) {
        return false
      }

      return selectionListType === targetType
    }

  return (
    <ActionBar aria-label="Formatting tools" className={styles.actionBar} gap="none">
      <ToolbarButton
        view={view}
        aria-label="Bold"
        icon={BoldIcon}
        command={formatBoldKeybinding}
        telemetryId="bold"
        matchingNodeFilter={n => n.type === 'strong'}
      />
      <ToolbarButton
        view={view}
        aria-label="Italic"
        icon={ItalicIcon}
        command={formatItalicKeybinding}
        telemetryId="italic"
        matchingNodeFilter={n => n.type === 'emphasis'}
      />
      <ToolbarButton
        view={view}
        aria-label="Link"
        icon={LinkIcon}
        command={formatLinkKeybinding}
        telemetryId="link"
        matchingNodeFilter={n => n.type === 'link' && isLabeledLink(n)}
      />
      <ToolbarButton
        view={view}
        aria-label="Code"
        icon={CodeIcon}
        command={formatInlineCodeKeybinding}
        telemetryId="inline_code"
        matchingNodeFilter={n => n.type === 'inlineCode'}
      />
      <ToolbarButton
        view={view}
        aria-label="Quote"
        icon={QuoteIcon}
        command={formatBlockquoteKeybinding}
        telemetryId="blockquote"
        matchingNodeFilter={n => n.type === 'blockquote'}
      />
      <ActionBar.Group>
        <ActionBar.Divider />
        <ToolbarButton
          view={view}
          aria-label="Unordered list"
          icon={ListUnorderedIcon}
          command={formatUnorderedListKeybinding}
          telemetryId="unordered_list"
          matchingNodeFilter={n => n.type === 'list' && !n.ordered && !isTaskList(n)}
          resolveIsPressed={resolveListIsPressed('unordered')}
        />
        <ToolbarButton
          view={view}
          aria-label="Ordered list"
          icon={ListOrderedIcon}
          command={formatOrderedListKeybinding}
          telemetryId="ordered_list"
          matchingNodeFilter={n => n.type === 'list' && !!n.ordered && !isTaskList(n)}
          resolveIsPressed={resolveListIsPressed('ordered')}
        />
        <ToolbarButton
          view={view}
          aria-label="Task list"
          icon={TasklistIcon}
          command={formatTaskListKeybinding}
          telemetryId="task_list"
          matchingNodeFilter={n => isTaskListItem(n)}
          resolveIsPressed={resolveListIsPressed('task')}
        />
      </ActionBar.Group>
      {/* This group of buttons are always in the overflow menu */}
      <ActionBar.Group>
        <ActionBar.Divider />
        {fileUploadButton}
        {/* <ToolbarButton onClick={() => {}} icon={ReplyIcon} aria-label="Saved replies" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={DiffIgnoredIcon} aria-label="Slash commands" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={AlertIcon} aria-label="Alert" /> */}
        <ToolbarButton
          view={view}
          aria-label="Code block"
          icon={CodeSquareIcon}
          command={formatCodeBlock}
          telemetryId="code_block"
          matchingNodeFilter={n => n.type === 'code'}
        />
        {/* <ToolbarButton onClick={() => {}} icon={TableIcon} aria-label="Table" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={MultiSelectIcon} aria-label="Details" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={SquareIcon} aria-label="Footnote" /> */}
        <HeadingMenu view={view} />
        {/* <ToolbarButton onClick={() => {}} icon={HorizontalRuleIcon} aria-label="Horizontal Rule" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={NumberIcon} aria-label="Math block" /> */}
        {/* <ToolbarButton onClick={() => {}} icon={CrossReferenceIcon} aria-label="Reference" /> */}
        <ToolbarButton
          view={view}
          aria-label="Strikethrough"
          icon={StrikethroughIcon}
          command={formatStrikethroughKeybinding}
          telemetryId="strikethrough"
          matchingNodeFilter={n => n.type === 'delete'}
        />
      </ActionBar.Group>
    </ActionBar>
  )
}
Toolbar.displayName = 'HybridMarkdownEditor.Toolbar'
